import React from 'react';

/**
 * Shared shell for bundled Administration hubs.
 * Provides a consistent header + tab bar and hosts the currently active sub-page.
 * The wrapped pages already ship their own headers, so we make ours slim to avoid
 * doubling up.
 */
const TONES = {
  emerald: { grad: 'from-emerald-500/[0.15] to-[#00E5FF]/[0.15]', dot: 'bg-emerald-400', text: 'text-emerald-300', ring: 'shadow-emerald-500/10', glow: 'rgba(16,185,129,0.15)' },
  cyan:    { grad: 'from-[#00E5FF]/[0.15] to-[#8A2BE2]/[0.12]', dot: 'bg-[#00E5FF]',   text: 'text-[#00E5FF]',   ring: 'shadow-cyan-500/10',    glow: 'rgba(0,229,255,0.14)' },
  amber:   { grad: 'from-amber-500/[0.15] to-rose-500/[0.10]',  dot: 'bg-amber-400',    text: 'text-amber-300',    ring: 'shadow-amber-500/10',   glow: 'rgba(245,158,11,0.15)' },
  violet:  { grad: 'from-[#8A2BE2]/[0.15] to-[#00E5FF]/[0.10]', dot: 'bg-[#c39be5]',    text: 'text-[#c39be5]',    ring: 'shadow-violet-500/10',  glow: 'rgba(138,43,226,0.15)' },
};

const HubShell = ({
  title,
  accent,
  subtitle,
  icon: Icon,
  tone = 'cyan',
  tabs = [],
  activeTab,
  onTabChange,
  dataTestId,
  headerRight,
  children,
}) => {
  const t = TONES[tone] || TONES.cyan;
  const visibleTabs = tabs.filter((x) => x.visible !== false);

  return (
    <div className="space-y-4 pb-6" data-testid={dataTestId}>
      {/* Slim header + tab strip */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12]">
        <div className={`pointer-events-none absolute -top-16 right-0 w-[380px] h-[220px] bg-gradient-to-br ${t.grad} rounded-full blur-[120px] opacity-70`} />
        <div className="relative px-5 py-4 flex items-center gap-3.5 border-b border-white/[0.04]">
          <div
            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.grad} border border-white/10 flex items-center justify-center flex-shrink-0`}
            style={{ boxShadow: `0 0 30px ${t.glow}` }}
          >
            {Icon && <Icon className={`w-4 h-4 ${t.text}`} strokeWidth={1.5} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Administration</p>
            <h1 className="text-xl font-light text-white tracking-tighter leading-tight">
              {title}{' '}
              {accent && (
                <span className={`bg-gradient-to-r ${t.grad.replace('/[0.15]', '').replace('/[0.12]', '').replace('/[0.10]', '')} bg-clip-text text-transparent font-medium`}>
                  {accent}
                </span>
              )}
            </h1>
            {subtitle && <p className="text-white/40 text-[11px] mt-0.5 truncate">{subtitle}</p>}
          </div>
          {headerRight && (
            <div className="flex-shrink-0 flex items-center">{headerRight}</div>
          )}
        </div>

        {/* Tabs */}
        <div className="relative flex items-center gap-1 px-3 py-2 overflow-x-auto no-scrollbar">
          {visibleTabs.map((tab) => {
            const TabIcon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                data-testid={tab.testId || `hub-tab-${tab.id}`}
                className={`group relative flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] tracking-[0.06em] font-medium whitespace-nowrap transition-all ${
                  active
                    ? `bg-white/[0.06] ${t.text} border border-white/[0.10]`
                    : 'text-white/45 hover:text-white/80 hover:bg-white/[0.03] border border-transparent'
                }`}
              >
                {active && <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />}
                {TabIcon && <TabIcon className="w-3.5 h-3.5" strokeWidth={1.75} />}
                <span>{tab.label}</span>
                {tab.badge != null && tab.badge > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold tabular-nums ${active ? 'bg-white/[0.08] text-white' : 'bg-white/[0.06] text-white/60'}`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active sub-page */}
      <div>{children}</div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default HubShell;
